import { FunctionComponent, useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import X from '~/components/shared/svg/X';
import Check from '~/components/shared/svg/Check';

import DEFAULT_PROPS from './data';
import TooltipV2 from '~/components/shared/TooltipV2';
import CTAButton, { CTAColorVariant } from '~/components/shared/CTAButton';
import { useMonarchSlotContext } from '@redventures/cohesion-utils-react';
import { useVariantIds } from '~/lib/hooks/useVariantIds';

interface CompareTableV2Props {
  headlineColor?: 'black' | 'blue';
  headline?: string;
  subheadline?: string;
  header1?: string;
  header2?: string;
  header3?: string;
  baseSiteComparisons?: {
    text: string;
    disclaimer: string;
    WalmartPlus: 'check' | 'x' | 'text';
    walmartText: string;
    Instacart: 'check' | 'x' | 'text';
    instacartText: string;
    Prime: 'check' | 'x' | 'text';
    primeText: string;
    Circle360: 'check' | 'x' | 'text';
    circle360Text: string;
  }[];
  primaryCTA?: {
    text: string;
    link: string;
    variant: CTAColorVariant;
  };
  secondaryCTA?: {
    text: string;
    link: string;
    variant: CTAColorVariant;
  };
}

const headerCellClasses = classNames(
  'text-digital-walmart-blue text-xl leading-[23px]'
);

const ctaWrapperClasses = classNames('pt-6 flex flex-col items-center gap-4');
const primaryCTAClasses = classNames('!w-[370px] text-center');
const secondaryCTAClasses = classNames('w-[370px] text-center');

const tableWrapperClasses = (canScrollRight: boolean) =>
  classNames(
    'relative w-full overflow-hidden',
    canScrollRight &&
      'after:absolute after:right-0 after:top-0 after:z-10 after:h-full after:w-4 after:bg-[linear-gradient(to_left,rgba(0,0,0,0.25),rgba(255,255,255,0))]'
  );

const tableScrollClasses = classNames(
  'overflow-x-auto',
  'scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-200'
);

const CompareTableV2: FunctionComponent<CompareTableV2Props> = () => {
  const { data } = useMonarchSlotContext<typeof DEFAULT_PROPS>();
  const { isDarkMode } = useVariantIds();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
      }
    };

    // Initial check
    handleScroll();

    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      }
    };
  }, []);

  // Pre-computed class names
  const wrapperClasses = classNames(
    'w-full max-w-[1920px] mx-auto py-4 px-4 lg:px-8',
    isDarkMode ? 'bg-dark-mode-background text-white' : 'bg-white'
  );

  const contentWrapperClasses = classNames(
    'mx-auto flex flex-col items-center'
  );

  const headlineClasses = classNames(
    'mb-0 text-center',
    isDarkMode ? 'text-white' : ''
  );

  const subheadlineClasses = classNames(
    'text-center leading-[19px]',
    isDarkMode ? 'text-white' : 'text-grey-4'
  );

  const tableContainerClasses = classNames(
    'mt-12 rounded-2xl py-2 lg:mt-16 w-full md:w-auto overflow-x-auto scrollbar',
    isDarkMode ? 'border-2 border-white/20' : 'border-2 border-[#e3e4e5]'
  );

  const headerRowClasses = classNames(
    '[&>:nth-child(2)]:bg-[#ffef99] [&>:nth-child(2)]:rounded-t-lg',
    isDarkMode ? 'text-white' : ''
  );

  const dataRowClasses = classNames(
    '[&>:nth-child(2)]:bg-[#ffef99]',
    'group relative',
    isDarkMode ? 'text-white' : ''
  );

  const dataCellClasses = classNames(
    'text-base leading-[19px] font-medium text-left p-4',
    isDarkMode ? 'text-white' : 'text-ozark-noir'
  );

  const compHeaderCellClasses = classNames(
    'text-base leading-[19px]',
    isDarkMode ? 'text-white' : 'text-grey-3'
  );

  const tableBodyClasses = classNames(
    '[&>tr>td]:m-0 [&>tr>td]:p-0',
    '[&>tr>td:first-child]:min-w-[160px] lg:[&>tr>td:first-child]:min-w-[282px]',
    '[&>tr>td:not(:first-child)]:min-w-[114px] [&>tr>td:not(:first-child)]:text-center',
    '[&>tr>th:first-child]:min-w-[160px] lg:[&>tr>th:first-child]:min-w-[282px] [&>tr>th:first-child]:p-4',
    '[&>tr>th:not(:first-child)]:min-w-[114px] [&>tr>th:not(:first-child)]:w-[204px] [&>tr>th:not(:first-child)]:h-[50px] [&>tr>th:not(:first-child)]:py-2 [&>tr>th:not(:first-child)]:px-[18px]',
    '[&>tr>td:nth-child(n+3)]:border-r [&>tr>td:nth-child(n+3)]:border-[#e6e6e6]',
    '[&>tr>td:nth-child(2)]:border-0',
    '[&>tr>td]:border-0',
    '[&>tr>td:first-child]:sticky [&>tr>td:first-child]:left-0 [&>tr>td:first-child]:z-10',
    isDarkMode
      ? '[&>tr>td:first-child]:bg-dark-mode-background'
      : '[&>tr>td:first-child]:bg-white',
    '[&>tr>th:first-child]:sticky [&>tr>th:first-child]:left-0 [&>tr>th:first-child]:z-10',
    '[&>tr:last-child>td:nth-child(2)]:rounded-b-lg'
  );

  const {
    headlineColor,
    headline,
    subheadline,
    header1,
    header2,
    header3,
    baseSiteComparisons,
    primaryCTA,
    secondaryCTA,
  } = data ?? DEFAULT_PROPS;

  const comparisons = baseSiteComparisons;
  const getHeadlineColor =
    headlineColor === 'blue' ? 'text-digital-walmart-blue' : 'text-black';

  const getContentType = (type: string, text: string) => {
    switch (type) {
      case 'check':
        return (
          <Check
            className={`h-6 w-6 mx-auto ${
              isDarkMode ? '[&>path]:fill-white' : '[&>path]:fill-[#848484]'
            }`}
          />
        );
        break;
      case 'x':
        return (
          <X
            className={`h-6 w-6 mx-auto ${
              isDarkMode ? '[&>path]:fill-white' : '[&>path]:fill-[#848484]'
            }`}
          />
        );
        break;
      case 'text':
        return (
          <p
            className={`text-base leading-[18px] font-medium m-0 ${
              isDarkMode ? 'text-white' : 'text-grey-3'
            }`}
          >
            {text}
          </p>
        );
        break;
    }
  };

  return (
    <div className={wrapperClasses}>
      <div className={contentWrapperClasses}>
        {headline && (
          <h2 className={`${getHeadlineColor} ${headlineClasses}`}>
            {headline}
          </h2>
        )}
        {subheadline && <p className={subheadlineClasses}>{subheadline}</p>}
        <div
          className={`${tableContainerClasses} ${tableWrapperClasses(
            canScrollRight
          )}`}
        >
          <div ref={scrollRef} className={tableScrollClasses}>
            <table
              role='table'
              className='border-collapse'
              aria-label='Compare Walmart+ membership to shopping without membership'
            >
              <tbody className={tableBodyClasses}>
                <tr className={headerRowClasses}>
                  <td>&nbsp;</td>
                  <th scope='col' className={headerCellClasses}>
                    Walmart+
                  </th>
                  {header1 && (
                    <th scope='col' className={compHeaderCellClasses}>
                      {header1}
                    </th>
                  )}
                  {header2 && (
                    <th scope='col' className={compHeaderCellClasses}>
                      {header2}
                    </th>
                  )}
                  {header3 && (
                    <th scope='col' className={compHeaderCellClasses}>
                      {header3}
                    </th>
                  )}
                </tr>
                {comparisons.map((comparison, index) => (
                  <tr key={comparison.text} className={dataRowClasses}>
                    <th
                      scope='row'
                      aria-label={comparison.text}
                      className={`${dataCellClasses} group-hover:z-20 ${
                        isDarkMode ? 'bg-dark-mode-background' : 'bg-white'
                      }`}
                    >
                      {comparison.text}
                      {comparison.disclaimer && (
                        <TooltipV2
                          text={comparison.disclaimer}
                          elementPosition={`COMPARE_TABLE_V2_${index + 1}`}
                          className={isDarkMode ? 'fill-white' : 'fill-black'}
                        />
                      )}
                    </th>
                    <td>
                      {comparison.WalmartPlus === 'check' ? (
                        <Check className='h-6 w-6 mx-auto [&>path]:fill-digital-walmart-blue' />
                      ) : comparison.WalmartPlus === 'x' ? (
                        <X className='h-6 w-6 mx-auto [&>path]:fill-digital-walmart-blue' />
                      ) : (
                        <p className='text-base leading-[18px] font-medium m-0'>
                          {comparison.walmartText}
                        </p>
                      )}
                    </td>

                    {header1 && (
                      <td>
                        {getContentType(comparison.Prime, comparison.primeText)}
                      </td>
                    )}
                    {header2 && (
                      <td>
                        {getContentType(
                          comparison.Circle360,
                          comparison.circle360Text
                        )}
                      </td>
                    )}
                    {header3 && (
                      <td>
                        {getContentType(
                          comparison.Instacart,
                          comparison.instacartText
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className={ctaWrapperClasses}>
          {primaryCTA && primaryCTA.text && (
            <CTAButton
              elementPosition='COMPARE_TABLE_V2'
              elementName='CTA'
              variant={primaryCTA.variant}
              elementType='SIGN_UP_CTA_PRIMARY'
              className={primaryCTAClasses}
              href={primaryCTA.link}
            >
              {primaryCTA.text}
            </CTAButton>
          )}
          <div>
            {secondaryCTA && secondaryCTA.text && (
              <CTAButton
                elementPosition='COMPARE_TABLE_V2'
                elementName='CTA'
                variant={secondaryCTA.variant}
                elementType='SIGN_UP_CTA_SECONDARY'
                href={secondaryCTA.link}
                className={secondaryCTAClasses}
              >
                {secondaryCTA.text}
              </CTAButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompareTableV2;
